import { Box, Card, Chip, Skeleton, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useMemo, useState } from "react";

import { ComplianceCard } from "./ComplianceCard";
import { type ComplianceItem, sortByDeadlineAscending } from "./CompliancePane.helpers";

type TypeFilter = "all" | "missing" | "epaCorrection";

export interface CompliancePaneProps {
  items: ComplianceItem[];
  now: Date;
  activePreviewDocId?: string | null;
  onViewDocument: (documentId: string, fileExtension: string | null) => void;
  onResolveInEManifest: (discrepancyId: string) => void;
  onAddProofOfContact: (processedManifestId: string) => void;
  onCreateExceptionReport: (processedManifestId: string) => void;
  creatingExceptionReport?: boolean;
  loading?: boolean;
}

export function CompliancePane(props: CompliancePaneProps) {
  const { items, now } = props;
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("all");

  const counts = useMemo(() => {
    let missing = 0;
    let epa = 0;
    for (const it of items) {
      if (it.kind === "missing") missing++;
      else epa++;
    }
    return { all: items.length, missing, epaCorrection: epa };
  }, [items]);

  const visibleItems = useMemo(() => {
    const filtered =
      typeFilter === "all"
        ? items
        : items.filter((i) =>
            typeFilter === "missing" ? i.kind === "missing" : i.kind === "epaCorrection"
          );
    return sortByDeadlineAscending(filtered, now);
  }, [items, typeFilter, now]);

  return (
    <Box sx={{ p: 1.5 }}>
      <Stack spacing={0.75} sx={{ mb: 1.5 }}>
        <Typography variant="caption" color="text.secondary">
          Type
        </Typography>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Chip
            size="small"
            label={`All (${counts.all})`}
            variant="outlined"
            color={typeFilter === "all" ? "info" : "default"}
            sx={
              typeFilter === "all"
                ? { bgcolor: (t) => alpha(t.palette.info.main, 0.08) }
                : undefined
            }
            onClick={() => setTypeFilter("all")}
          />
          <Chip
            size="small"
            label={`EPA Correction (${counts.epaCorrection})`}
            variant="outlined"
            color={typeFilter === "epaCorrection" ? "info" : "default"}
            sx={
              typeFilter === "epaCorrection"
                ? { bgcolor: (t) => alpha(t.palette.info.main, 0.08) }
                : undefined
            }
            onClick={() => setTypeFilter("epaCorrection")}
          />
          <Chip
            size="small"
            label={`Missing e-Manifest (${counts.missing})`}
            variant="outlined"
            color={typeFilter === "missing" ? "info" : "default"}
            sx={
              typeFilter === "missing"
                ? { bgcolor: (t) => alpha(t.palette.info.main, 0.08) }
                : undefined
            }
            onClick={() => setTypeFilter("missing")}
          />
          <Box sx={{ flexGrow: 1 }} />
          <Typography variant="caption" color="text.secondary">
            Sorted by deadline ↑
          </Typography>
        </Stack>
      </Stack>

      {renderBody({
        visibleItems,
        itemsLength: items.length,
        loading: props.loading ?? false,
        now,
        activePreviewDocId: props.activePreviewDocId,
        onViewDocument: props.onViewDocument,
        onResolveInEManifest: props.onResolveInEManifest,
        onAddProofOfContact: props.onAddProofOfContact,
        onCreateExceptionReport: props.onCreateExceptionReport,
        creatingExceptionReport: props.creatingExceptionReport,
      })}
    </Box>
  );
}

function renderBody(params: {
  visibleItems: ComplianceItem[];
  itemsLength: number;
  loading: boolean;
  now: Date;
  activePreviewDocId: string | null | undefined;
  onViewDocument: (documentId: string, fileExtension: string | null) => void;
  onResolveInEManifest: (discrepancyId: string) => void;
  onAddProofOfContact: (processedManifestId: string) => void;
  onCreateExceptionReport: (processedManifestId: string) => void;
  creatingExceptionReport: boolean | undefined;
}) {
  const { visibleItems, itemsLength, loading, now } = params;

  if (visibleItems.length > 0) {
    return (
      <Stack spacing={3}>
        {visibleItems.map((item) => (
          <ComplianceCard
            key={item.id}
            item={item}
            now={now}
            activePreviewDocId={params.activePreviewDocId}
            onViewDocument={params.onViewDocument}
            onResolveInEManifest={params.onResolveInEManifest}
            onAddProofOfContact={params.onAddProofOfContact}
            onCreateExceptionReport={params.onCreateExceptionReport}
            creatingExceptionReport={params.creatingExceptionReport}
          />
        ))}
      </Stack>
    );
  }

  if (loading && itemsLength === 0) {
    return (
      <Stack spacing={3}>
        {Array.from({ length: 3 }, (_, i) => (
          <Card key={i} variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={1.5}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Skeleton variant="rounded" width={88} height={24} />
                <Skeleton variant="rounded" width={72} height={24} />
                <Box sx={{ flexGrow: 1 }} />
                <Skeleton variant="text" width={120} />
              </Stack>
              <Skeleton variant="text" width="60%" />
              <Skeleton variant="text" width="40%" />
              <Stack direction="row" spacing={1}>
                <Skeleton variant="rounded" width={120} height={32} />
                <Skeleton variant="rounded" width={140} height={32} />
              </Stack>
            </Stack>
          </Card>
        ))}
      </Stack>
    );
  }

  return (
    <Box sx={{ py: 6, textAlign: "center" }}>
      <Typography variant="body2" color="text.secondary">
        {itemsLength === 0
          ? "No compliance items for the selected manifest(s)."
          : "No items match the current filter."}
      </Typography>
    </Box>
  );
}
